import type { SendCustomEventFunction } from '@/utils/GA4-service'
import type { GetVerifiedUserIdFunction } from '@/utils/get-verified-user-id'
import type { AddSaveFunction, SyncSavesFunction } from '@/utils/simple-save-helpers'
import type { SendEventToStatsigFunction } from '@/utils/statsig'
import type { AlpineComponent } from 'alpinejs'

interface Auth0Identity {
  user_id: string
  provider: string
  connection: string
  isSocial: boolean
}

export interface AboutYou {
  title?: string
  dob?: string
  recipes_for_children?: number | string
}

export interface UserMetadata {
  [key: string]: string | undefined | AboutYou
}

export interface AuthUser {
  created_at: string
  updated_at: string
  sub?: string
  user_id: string
  email: string
  email_verified: boolean
  identities: Auth0Identity[]
  name: string
  nickname: string
  picture: string
  user_metadata: UserMetadata
}

export interface AuthTokenResponse {
  access_token: string
  expires_in: number
  token_type: string
  scope?: string
  id_token?: string
}

export interface AuthForm {
  email: string
  password: string
  firstName: string
  lastName: string
  opt_in_core: boolean
}

export interface AuthAlert {
  title: string
  description: string
}

export interface AuthErrors {
  email: string
  password: string
  firstName: string
  lastName: string
}

export const DefaultAuthFormState = (): AuthForm => ({
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  opt_in_core: false,
})

export const DefaultAuthAlertState = (): AuthAlert => ({
  title: '',
  description: '',
})

export const DefaultAuthErrorsState = (): AuthErrors => ({
  email: '',
  password: '',
  firstName: '',
  lastName: '',
})

export const defaultAboutYou = (): AboutYou => ({
  title: '',
  dob: '',
  recipes_for_children: '',
})

export interface Auth0JwtPayload {
  iss: string
  sub: string
  aud: string[]
  iat: number
  exp: number
  scope: string
  gty: string
  azp: string
}

type PasswordCriteria = {
  condition: (value: string) => boolean
  message: string
  met: boolean
  isVisible: boolean
}

type AvailableSocialLoginServices = 'google-oauth2' | 'apple' | 'facebook'
type SocialLoginService = { key: AvailableSocialLoginServices; value: string }

type AvailableAuthFormSteps = 'idle' | 'login' | 'register' | 'resetPassword' | 'validateEmail'

export type AuthFormComponent = {
  form: AuthForm
  user: any
  handlerService: null | any
  steps: AvailableAuthFormSteps
  currentStep: number
  previousStep: number
  message: AuthAlert
  errors: AuthErrors
  isLoading: boolean
  redirectLogin: boolean
  loadingSocial: null | string
  socials: Array<SocialLoginService>
  root: HTMLElement
  criterias: Array<PasswordCriteria>
  context: string | null
  metadata: Record<string, any> | null
  setMessage: (title: string, description: string, timeout?: number | undefined) => void
  handleResponseError: (title: string, result: Record<string, any> | string | null) => void
  nextStep: (step: AvailableAuthFormSteps) => void
  $dispatch: (event: string) => void
  close: () => void
  klaviyoTrack: (event: string, data?: Record<string, any>) => void
}

export type AuthComponent = AlpineComponent<AuthFormComponent>

export type AuthHandlerServiceHelpers = {
  sendCustomEvent: SendCustomEventFunction
  sendEventToStatsig: SendEventToStatsigFunction
  addSave: AddSaveFunction
  syncSaves: SyncSavesFunction
  getVerifiedUserId: GetVerifiedUserIdFunction
}
